import { useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import api from '../api';

/* ===========================================================================
   In-app announcement surface. Fetches the active, audience-matched, not-yet-
   dismissed announcement once on mount and renders it as a tasteful modal or a
   quiet top banner. Dismissal and CTA clicks are recorded so it never nags.
   Mounted in the app shell so it can appear on any authenticated screen.

   The presentational piece (AnnouncementView) is exported so the admin console
   can preview an announcement on demand using the EXACT same markup and styles
   users see — no divergence between preview and reality.
   =========================================================================== */

export type AnnouncementData = {
  id: string;
  headline: string;
  body: string;
  ctaLabel: string | null;
  ctaHref: string | null;
  style: 'modal' | 'banner';
};

export function AnnouncementSurface({ authed }: { authed: boolean }) {
  const [, navigate] = useLocation();
  const [ann, setAnn] = useState<AnnouncementData | null>(null);

  useEffect(() => {
    if (!authed) { setAnn(null); return; }
    let cancelled = false;
    api.getActiveAnnouncement()
      .then((r) => { if (!cancelled) setAnn(r.announcement as AnnouncementData | null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [authed]);

  if (!ann) return null;

  const dismiss = () => {
    api.dismissAnnouncement({ announcementId: ann.id }).catch(() => {});
    setAnn(null);
  };

  const act = () => {
    if (!ann.ctaHref) return;
    api.pingAnnouncementClick({ announcementId: ann.id }).catch(() => {});
    const href = ann.ctaHref;
    setAnn(null);
    if (/^https?:\/\//i.test(href)) {
      window.open(href, '_blank', 'noopener,noreferrer');
    } else {
      navigate(href);
    }
  };

  return <AnnouncementView ann={ann} onDismiss={dismiss} onAct={act} />;
}

/**
 * Pure presentational render of an announcement (modal or banner) plus its
 * scoped styles and exit animation. Used both by the live surface and by the
 * admin preview, so what the operator previews is exactly what users get.
 *
 * - onDismiss: fired after the close animation (parent records + unmounts).
 * - onAct: fired immediately on CTA click (parent records + navigates, or, in
 *   preview, simply closes without navigating).
 * - previewNote: when set, a small caption pill is shown above the surface to
 *   signal "this is a preview" — the announcement itself is unchanged.
 */
export function AnnouncementView({
  ann,
  onDismiss,
  onAct,
  previewNote,
}: {
  ann: AnnouncementData;
  onDismiss: () => void;
  onAct: () => void;
  previewNote?: string;
}) {
  const [closing, setClosing] = useState(false);
  const close = () => {
    setClosing(true);
    setTimeout(onDismiss, 180);
  };

  const note = previewNote ? (
    <div className="ann-preview-note" role="status">{previewNote}</div>
  ) : null;

  if (ann.style === 'banner') {
    return (
      <div className={`ann-banner${closing ? ' is-closing' : ''}${previewNote ? ' ann-banner--preview' : ''}`} role="status">
        {note}
        <div className="ann-banner__inner">
          <div className="ann-banner__text">
            <strong>{ann.headline}</strong>
            <span>{ann.body}</span>
          </div>
          <div className="ann-banner__actions">
            {ann.ctaLabel && ann.ctaHref && (
              <button className="ann-banner__cta" onClick={onAct}>{ann.ctaLabel}</button>
            )}
            <button className="ann-banner__x" onClick={close} aria-label="Dismiss">✕</button>
          </div>
        </div>
        <AnnouncementStyles />
      </div>
    );
  }

  // Modal
  return (
    <div className={`ann-overlay${closing ? ' is-closing' : ''}`} onClick={close}>
      {note}
      <div className="ann-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="ann-h">
        <button className="ann-modal__x" onClick={close} aria-label="Dismiss">✕</button>
        <div className="ann-modal__mark" aria-hidden="true">
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none"><path d="M4 11a8 8 0 0 1 16 0v5l1.5 2.5a.5.5 0 0 1-.43.75H3a.5.5 0 0 1-.43-.75L4 16v-5z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" /><path d="M9.5 21a2.5 2.5 0 0 0 5 0" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
        </div>
        <h2 className="ann-modal__h" id="ann-h">{ann.headline}</h2>
        <p className="ann-modal__b">{ann.body}</p>
        <div className="ann-modal__actions">
          {ann.ctaLabel && ann.ctaHref && (
            <button className="ann-modal__cta" onClick={onAct}>{ann.ctaLabel}</button>
          )}
          <button className="ann-modal__dismiss" onClick={close}>{ann.ctaLabel ? 'Not now' : 'Got it'}</button>
        </div>
      </div>
      <AnnouncementStyles />
    </div>
  );
}

/** Scoped styles for the surface, injected once with the component. */
function AnnouncementStyles() {
  return (
    <style>{`
      /* Preview caption — only shown in admin preview mode */
      .ann-preview-note {
        position: fixed; top: 18px; left: 50%; transform: translateX(-50%); z-index: 260;
        background: var(--ink); color: var(--parchment); font-size: 12px; font-weight: 600;
        padding: 7px 14px; border-radius: 999px; box-shadow: 0 6px 20px rgba(15,23,20,0.25);
        letter-spacing: 0.01em; pointer-events: none;
      }

      /* Banner — quiet, pinned above app content */
      .ann-banner { background: var(--heartwood); color: var(--parchment); position: relative; z-index: 240; }
      /* In admin preview, pin the banner to the top of the viewport so it reads
         like it does live (it sits atop the app shell), not wherever the admin
         page happened to render it. */
      .ann-banner--preview { position: fixed; top: 0; left: 0; right: 0; z-index: 245; box-shadow: 0 8px 24px rgba(15,23,20,0.18); animation: ann-fade 180ms ease; }
      .ann-banner.is-closing { opacity: 0; transition: opacity 180ms ease; }
      .ann-banner__inner { max-width: 1180px; margin: 0 auto; padding: 11px 20px; display: flex; align-items: center; gap: 16px; justify-content: space-between; }
      .ann-banner__text { display: flex; align-items: baseline; gap: 10px; min-width: 0; flex-wrap: wrap; }
      .ann-banner__text strong { font-size: 14px; font-weight: 600; }
      .ann-banner__text span { font-size: 13.5px; opacity: 0.85; }
      .ann-banner__actions { display: flex; align-items: center; gap: 10px; flex-shrink: 0; }
      .ann-banner__cta { background: var(--spark); color: #06231a; border: 0; border-radius: 8px; padding: 7px 14px; font-size: 13px; font-weight: 700; cursor: pointer; font-family: inherit; white-space: nowrap; }
      .ann-banner__cta:hover { filter: brightness(1.06); }
      .ann-banner__x { background: none; border: 0; color: var(--parchment); opacity: 0.6; cursor: pointer; font-size: 14px; padding: 4px; line-height: 1; }
      .ann-banner__x:hover { opacity: 1; }

      /* Modal */
      .ann-overlay { position: fixed; inset: 0; z-index: 240; background: rgba(15,23,20,0.5); backdrop-filter: blur(3px); display: flex; align-items: center; justify-content: center; padding: 24px; animation: ann-fade 180ms ease; }
      .ann-overlay.is-closing { opacity: 0; transition: opacity 160ms ease; }
      @keyframes ann-fade { from { opacity: 0; } to { opacity: 1; } }
      .ann-modal { position: relative; width: 100%; max-width: 420px; background: var(--surface-card, #fbf8f0); border: 1px solid var(--border-hairline); border-radius: 18px; padding: 34px 30px 26px; text-align: center; box-shadow: 0 20px 60px rgba(15,23,20,0.22); animation: ann-pop 220ms cubic-bezier(0.22,1,0.36,1); }
      @keyframes ann-pop { from { transform: translateY(10px) scale(0.98); opacity: 0; } to { transform: none; opacity: 1; } }
      .ann-modal__x { position: absolute; top: 14px; right: 16px; background: none; border: 0; color: var(--text-decorative); font-size: 15px; cursor: pointer; padding: 4px; line-height: 1; }
      .ann-modal__x:hover { color: var(--ink); }
      .ann-modal__mark { width: 52px; height: 52px; border-radius: 14px; background: color-mix(in srgb, var(--heartwood) 8%, transparent); color: var(--heartwood); display: flex; align-items: center; justify-content: center; margin: 0 auto 18px; }
      .ann-modal__h { font-family: 'Sentient', serif; font-weight: 500; font-size: 25px; line-height: 1.15; letter-spacing: -0.015em; color: var(--ink); margin: 0 0 10px; }
      .ann-modal__b { font-size: 15px; line-height: 1.55; color: var(--text-body, #3a4038); margin: 0 auto 24px; max-width: 34ch; }
      .ann-modal__actions { display: flex; flex-direction: column; gap: 10px; }
      .ann-modal__cta { background: var(--heartwood); color: var(--parchment); border: 0; border-radius: 10px; padding: 13px 20px; font-size: 15px; font-weight: 600; cursor: pointer; font-family: inherit; transition: filter 120ms ease; }
      .ann-modal__cta:hover { filter: brightness(1.08); }
      .ann-modal__dismiss { background: none; border: 0; color: var(--text-decorative); font-size: 13.5px; font-weight: 500; cursor: pointer; font-family: inherit; padding: 4px; }
      .ann-modal__dismiss:hover { color: var(--ink); }
      @media (max-width: 560px) {
        .ann-banner__inner { flex-wrap: wrap; }
      }
    `}</style>
  );
}
